/**
 * CIEM API module - Cloud Infrastructure Entitlement Management.
 *
 * Real-split (was a stub re-export from ../api). Hosts the canonical
 * implementations of ciemApi, crossCloudApi, accessAnomalyApi, and
 * ciemRightsizingApi. Public signatures unchanged.
 */
import { _unwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';
import type { CloudProvider, PaginatedResponse, IdentityRisk } from '../../types';

export const ciemApi = {
  getIdentities: async (params?: {
    provider?: CloudProvider;
    identity_type?: string;
    page?: number;
    page_size?: number;
    // The handler paginates on `limit`; `page_size` is accepted but ignored,
    // so a page_size-only call silently returned the full identity set.
    limit?: number;
  }): Promise<PaginatedResponse<IdentityRisk>> => {
    return _unwrap(await fetchClient.get<PaginatedResponse<IdentityRisk>>('/api/v1/ciem/identities', params as any));
  },

  getIdentity: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/identities/${id}`));
  },

  // Permission Analyzer
  getPermissions: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/permissions/${identityId}`));
  },

  analyzePermissions: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ciem/permissions/${identityId}/analyze`));
  },

  getRecommendations: async (identityId: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>(`/api/v1/ciem/permissions/${identityId}/recommendations`));
  },

  // Access Governance
  getAccessReviews: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ciem/access/reviews'));
  },

  createAccessReview: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/access/reviews', data));
  },

  // Privilege Escalation Detection
  getPrivilegePaths: async (identityId?: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ciem/privilege-paths', { identity_id: identityId } as any));
  },

  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/identities/statistics'));
  },

  // High-Risk & Filtered Identities
  getHighRiskIdentities: async (params?: { min_risk_score?: number; limit?: number }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ciem/high-risk-identities', params as any));
  },

  getAdminIdentities: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ciem/admin-identities'));
  },

  getIdentitiesWithoutMFA: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ciem/identities-without-mfa'));
  },

  getInactiveIdentities: async (params?: { days_threshold?: number }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ciem/inactive-identities', params as any));
  },

  getCrossAccountAccess: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ciem/cross-account-access'));
  },

  getRisks: async (params?: { severity?: string; status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ciem/risks', params as any));
  },

  getAllRecommendations: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ciem/recommendations'));
  },

  // Effective Permissions — use the bare /ciem/effective-permissions/{id} form.
  // The gateway rewrites it to /effective-permissions/api/v1/cached/{id} (a real
  // backend route). The /ciem/identities/{id}/... form was swallowed by the
  // longer /ciem/identities prefix rewrite → identity-service 404.
  getEffectivePermissions: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/effective-permissions/${encodeURIComponent(identityId)}`));
  },

  // Unified Dashboard endpoints
  getFullAnalysis: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/identity/${identityId}/full-analysis`));
  },

  getRiskPosture: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/risk-posture'));
  },

  getLeastPrivilegeOpportunities: async (): Promise<any[]> => {
    // The dedicated /least-privilege-opportunities route lives in a ciem child
    // that isn't mounted on the aggregator (404). Over-privileged identities ARE
    // the least-privilege opportunities — source them from the working endpoint.
    const res: any = _unwrap(await fetchClient.get<any>('/api/v1/ciem/over-privileged'));
    return Array.isArray(res) ? res : (res?.identities || res?.opportunities || []);
  },

  getTrends: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/trends'));
  },

  analyzeAll: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/analyze-all'));
  },

  // Permission Analyzer Advanced
  getRightsizing: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/rightsizing/${identityId}`));
  },

  getRiskyPermissions: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ciem/risky-permissions'));
  },

  getPermissionStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/permission-statistics'));
  },

  // Anomaly Detection (Port 8037)
  getAnomalies: async (params?: {
    page?: number;
    page_size?: number;
    severity?: string;
    anomaly_type?: string;
    is_resolved?: boolean
  }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/anomalies', params as any));
  },

  getAnomaly: async (anomalyId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/anomalies/${anomalyId}`));
  },

  resolveAnomaly: async (anomalyId: string, data: { resolved_by: string; resolution_notes: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ciem/anomalies/${anomalyId}/resolve`, data));
  },

  getAnomalyStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/anomalies/statistics'));
  },

  triggerDetection: async (data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/anomalies/detect', data || {}));
  },

  // Baselines
  getBaselines: async (params?: { identity_id?: string; baseline_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/baselines', params as any));
  },

  getIdentityBaselines: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/baselines/${identityId}`));
  },

  recalculateBaseline: async (identityId: string, data: { baseline_type: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ciem/baselines/${identityId}/recalculate`, data));
  },

  // NHI (Non-Human Identity) Management
  getNHIIdentities: async (params?: { nhi_type?: string; risk_level?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/nhi/identities', params as any));
  },

  getNHIStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/nhi/statistics'));
  },

  // JIT (Just-In-Time) Access
  getJITRequests: async (params?: { status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/jit/requests', params as any));
  },

  getJITTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/jit/templates'));
  },

  getJITAnalytics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/jit/analytics'));
  },

  // R136 / G-JIT-FANOUT: full workflow (request/approve/deny/revoke/extend/break-glass/audit)
  getJITRequest: async (requestId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/jit/requests/${requestId}`));
  },
  createJITRequest: async (data: { template_id?: string; requested_role?: string; resource_arn?: string; duration_hours?: number; justification?: string; provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/jit/request', data));
  },
  approveJITRequest: async (requestId: string, data?: { approver_notes?: string; approved_duration_hours?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ciem/jit/${requestId}/approve`, data || {}));
  },
  denyJITRequest: async (requestId: string, data: { reason: string; approver_notes?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ciem/jit/${requestId}/deny`, data));
  },
  revokeJITRequest: async (requestId: string, data?: { reason?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ciem/jit/${requestId}/revoke`, data || {}));
  },
  extendJITRequest: async (requestId: string, data: { extension_hours: number; justification?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ciem/jit/${requestId}/extend`, data));
  },
  getActiveJITGrants: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/jit/active'));
  },
  getPendingJITApprovals: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/jit/pending-approvals'));
  },
  createBreakGlassAccess: async (data: { resource_arn: string; reason: string; duration_minutes?: number; provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/jit/break-glass', data));
  },
  getBreakGlassHistory: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/jit/break-glass'));
  },
  getJITAuditLog: async (params?: { request_id?: string; actor?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/jit/audit', params as any));
  },

  // Identity Threat Detection
  getIdentityThreats: async (params?: { severity?: string; threat_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/threats', params as any));
  },

  getIdentityThreatSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/threats/summary'));
  },

  // Federated Identity Analysis
  getFederatedIdentities: async (params?: { provider_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/federated-identities', params as any));
  },

  // Resource-Based Policy Analysis
  getResourcePolicies: async (params?: { resource_type?: string; has_public_access?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/resource-policies', params as any));
  },

  // Identity-to-Data Access Mapping
  getIdentityDataAccess: async (params?: { classification?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/identity-data-access', params as any));
  },

  getIdentityDataAccessDetail: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/identity-data-access/${identityId}`));
  },

  // Cross-Cloud Permission Normalization (via aggregator proxy → cross-cloud-access:8039)
  getNormalizedPermissions: async (params?: { provider?: string; category?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/cross-cloud/permissions/normalized', params as any));
  },

  normalizePermissions: async (data: { provider: string; permissions: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/cross-cloud/permissions/normalize', data));
  },

  getIdentityNormalizedPermissions: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/cross-cloud/permissions/normalized/${identityId}`));
  },

  // Federation Mappings
  getFederationMappings: async (params?: { provider?: string; type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/cross-cloud/federation/mappings', params as any));
  },

  createFederationMapping: async (data: { source_provider: string; target_provider: string; federation_type: string; config: any }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/cross-cloud/federation/mappings', data));
  },

  // Trust Relationships
  getTrustRelationships: async (params?: { provider?: string; risk_level?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/cross-cloud/trust-relationships', params as any));
  },

  analyzeTrustChain: async (data: { source_identity: string; target_resource: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/cross-cloud/trust-relationships/analyze', data));
  },

  // Role Chaining Detection
  getRoleChaining: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/cross-cloud/role-chaining'));
  },

  // M365 / Entra ID (via aggregator proxy → m365-identity-scanner:8034)
  getM365Users: async (params?: { department?: string; risk_level?: string; mfa_status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/users', params as any));
  },

  getM365User: async (userId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/m365/users/${userId}`));
  },

  getM365RiskyUsers: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/users/risky'));
  },

  getM365StaleUsers: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/users/stale'));
  },

  // Entra ID Conditional Access
  getConditionalAccessPolicies: async (params?: { status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/conditional-access/policies', params as any));
  },

  createConditionalAccessPolicy: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/m365/conditional-access/policies', data));
  },

  getConditionalAccessPolicy: async (policyId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/m365/conditional-access/policies/${policyId}`));
  },

  updateConditionalAccessPolicy: async (policyId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/ciem/m365/conditional-access/policies/${policyId}`, data));
  },

  getConditionalAccessGaps: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/conditional-access/gaps'));
  },

  simulateConditionalAccess: async (params: { user_id: string; app_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/conditional-access/simulation', params as any));
  },

  // M365 App Permissions
  getM365Apps: async (params?: { app_type?: string; risk_level?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/apps', params as any));
  },

  getM365RiskyApps: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/apps/risky'));
  },

  getM365RiskyOAuthApps: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/apps/risky-oauth'));
  },

  getOverprivilegedApps: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/app-permissions/overprivileged'));
  },

  getAppPermissionGraph: async (appId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/m365/app-permissions/${appId}/graph`));
  },

  // M365 MFA
  getM365MFAReport: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/mfa/report'));
  },

  getM365MFAGaps: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/mfa/gaps'));
  },

  // M365 Dashboard & Scanning
  getM365Dashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/dashboard'));
  },

  triggerM365Scan: async (): Promise<any> => {
    // Wiz-parity: unified data-plane primitive. Gateway proxies to
    // ciem-aggregator /api/v1/m365/scan.
    return _unwrap(await fetchClient.post<any>('/api/v1/data-plane/refresh/ciem', { provider: 'm365' }));
  },

  getM365SecurityPosture: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/m365/posture'));
  },

  // Okta Identity Scanner
  getOktaDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/okta/dashboard'));
  },
  getOktaPosture: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/okta/posture'));
  },
  getOktaIdentities: async (params?: { status?: string; has_mfa?: boolean; is_admin?: boolean; risk_min?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/okta/identities', params as any));
  },
  getOktaCloudMappings: async (params?: { cloud_provider?: string; is_admin?: boolean; is_over_privileged?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/okta/cloud-mappings', params as any));
  },
  getOktaFindings: async (params?: { severity?: string; finding_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/okta/findings', params as any));
  },
  triggerOktaScan: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/okta/scan'));
  },
  createOktaConfig: async (data: { org_url: string; api_token: string; name?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/okta/config', data));
  },

  // Google Workspace Identity Scanner
  getWorkspaceDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/workspace/dashboard'));
  },
  getWorkspaceIdentities: async (params?: { is_admin?: boolean; has_2sv?: boolean; has_gcp_access?: boolean; risk_min?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/workspace/identities', params as any));
  },
  getWorkspaceFindings: async (params?: { severity?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/workspace/findings', params as any));
  },
  getWorkspaceThreats: async (params?: { severity?: string; event_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/workspace/threats', params as any));
  },
  triggerWorkspaceScan: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/workspace/scan'));
  },
  createWorkspaceConfig: async (data: { domain: string; name?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ciem/workspace/config', data));
  },

  // Activity Log
  getActivityLog: async (identityId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ciem/identities/${identityId}/activity-log`));
  },

  // Detection Rules
  getDetectionRules: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ciem/detection-rules'));
  },

  // Permission boundaries
  applyPermissionBoundary: async (data: { identity_arn: string; boundary_policy_arn: string }) =>
    _unwrap(await fetchClient.post<any>('/api/v1/ciem/permission-boundaries/apply', data)),
  getMissingPermissionBoundaries: async (params?: { account_id?: string }) =>
    _unwrap(await fetchClient.get<any>('/api/v1/ciem/permission-boundaries/missing', params as any)),
  generatePermissionBoundary: async (data: { identity_id: string }) =>
    _unwrap(await fetchClient.post<any>('/api/v1/ciem/permission-boundaries/generate', data)),
  // Key rotation
  rotateAccessKey: async (data: { identity_id: string; user_name: string; old_key_id: string }) =>
    _unwrap(await fetchClient.post<any>('/api/v1/ciem/keys/rotate', data)),
  getStaleKeys: async (params?: { max_age_days?: number }) =>
    _unwrap(await fetchClient.get<any>('/api/v1/ciem/keys/stale', params as any)),
  setKeyRotationPolicy: async (data: { max_age_days: number; auto_rotate: boolean; notification_days_before: number }) =>
    _unwrap(await fetchClient.post<any>('/api/v1/ciem/keys/rotation-policy', data)),
  getKeyRotationPolicy: async () =>
    _unwrap(await fetchClient.get<any>('/api/v1/ciem/keys/rotation-policy')),
  // Access certification
  getAccessReviewItems: async (reviewId: string) =>
    _unwrap(await fetchClient.get<any>(`/api/v1/ciem/access-reviews/${reviewId}/items`)),
  decideAccessReviewItem: async (reviewId: string, itemId: string, data: { decision: string; reason?: string }) =>
    _unwrap(await fetchClient.post<any>(`/api/v1/ciem/access-reviews/${reviewId}/items/${itemId}/decide`, data)),
  getAccessReviewProgress: async (reviewId: string) =>
    _unwrap(await fetchClient.get<any>(`/api/v1/ciem/access-reviews/${reviewId}/progress`)),
};

// ── Cross-Cloud Attack Paths API ─────────────────────────────────────────────
export const crossCloudApi = {
  getPaths: async (minSeverity?: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/graph/cross-cloud-paths', { min_severity: minSeverity }));
  },
  analyze: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/graph/cross-cloud-paths/analyze', {}));
  },
};

// R125: accessAnomalyApi deprecated and removed. The wrapper pointed at
// /api/v1/ciem/access/{baseline,analyze,anomalies,baselines} which never
// had BE handlers. The actual identity-anomaly feature is served by
// ciemApi.{getAnomalies, getAnomalyStatistics, triggerDetection, getBaselines}
// at /api/v1/ciem/anomalies* (CIEM anomaly-detection service), wired into
// R45 AnomalyDashboard.tsx.

// ── CIEM Right-Sizing API ──
// Migrated to the canonical fetch client (services/api/client.ts).
// Public signatures unchanged: throws on non-2xx, returns unwrapped data.
export const ciemRightsizingApi = {
    // Analyze reads IAM users/roles from inventory and persists least-privilege
    // recommendations to csp_ciem.rightsizing_recommendations. The BE endpoint
    // ignores the body (analyzes the whole tenant), so args are optional.
    analyze: async (data?: { entity_type?: string; entity_id?: string }) => _unwrap(await fetchClient.post<any>('/api/v1/ciem/rightsizing/analyze', data || {})),
    getRecommendations: async () => _unwrap(await fetchClient.get<any>('/api/v1/ciem/rightsizing/recommendations')),
    getRecommendation: async (id: string) => _unwrap(await fetchClient.get<any>(`/api/v1/ciem/rightsizing/recommendations/${id}`)),
    apply: async (id: string) => _unwrap(await fetchClient.post<any>(`/api/v1/ciem/rightsizing/apply/${id}`)),
    getStats: async () => _unwrap(await fetchClient.get<any>('/api/v1/ciem/rightsizing/stats')),
    simulate: async (data: any) => _unwrap(await fetchClient.post<any>('/api/v1/ciem/rightsizing/simulate', data)),
};
